import { useState, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import type { List as ImmutableList, Map as ImmutableMap } from 'immutable';

import SmartToyIcon from '@/material-icons/400-24px/smart_toy.svg?react';
import { Icon } from 'mastodon/components/icon';

import { describeAiSource } from './ai_source';

type ImmutableStatus = ImmutableMap<string, unknown>;

// The status-level label. A post counts as AI-labelled when the status
// itself is (compose toggle / text detector) OR any attachment is (file
// provenance / image classifier). Pure function so the feed test and the
// component share one definition.
export const aiLabelSourceForStatus = (
  status: ImmutableStatus,
): string | null => {
  if (status.get('ai_generated') === true) {
    return (status.get('ai_generated_source') as string | null) ?? 'unknown';
  }

  const attachments = status.get('media_attachments') as
    | ImmutableList<ImmutableMap<string, unknown>>
    | undefined;

  const flagged = attachments?.find((a) => a.get('ai_generated') === true);

  if (flagged) {
    return (flagged.get('ai_generated_source') as string | null) ?? 'unknown';
  }

  return null;
};

export const StatusAiLabel: React.FC<{ status: ImmutableStatus }> = ({
  status,
}) => {
  const [open, setOpen] = useState(false);
  const source = aiLabelSourceForStatus(status);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpen((v) => !v);
    },
    [setOpen],
  );

  if (!source) return null;

  const mediaOnly = status.get('ai_generated') !== true;

  return (
    <div className='status__ai-label' data-testid='status-ai-label'>
      <button
        type='button'
        className='status__ai-label__button'
        onClick={handleClick}
        aria-expanded={open}
      >
        <Icon id='smart-toy' icon={SmartToyIcon} />
        {mediaOnly ? (
          <FormattedMessage
            id='status.ai_generated_media'
            defaultMessage='Contains AI-generated media'
          />
        ) : (
          <FormattedMessage
            id='status.ai_generated'
            defaultMessage='AI-generated content'
          />
        )}
      </button>
      {open && (
        <div className='status__ai-label__detail'>
          {describeAiSource(source)}
        </div>
      )}
    </div>
  );
};
